import type { Character, CharacterSkill, CrawlerType } from '../domain/character.ts';
import type { CharacterChange } from '../domain/characterChange.ts';
import type { StatBlock } from '../domain/stats.ts';
import { STAT_IDS } from '../domain/stats.ts';
import type { Skill } from '../content/types.ts';
import type { ContentRegistry } from '../content/registry.ts';
import { isLegalSize } from '../rules/size.ts';
import { isSkillLegalFor } from '../rules/skillAvailability.ts';

/**
 * Turns the Edit Character screen's form values into a batch of
 * `CharacterChange` records.
 *
 * This is the ONLY thing the editing screen knows how to do. It holds no rules:
 * every change it produces is checked by `src/rules/characterChanges.ts`, and a
 * batch it orders badly is refused there rather than half-applied. What lives
 * here is the translation from "the form now says X" into "these are the
 * described changes", plus the batch ORDERING needed for a legal sequence.
 *
 * It exists in `src/app/` for the same reason `creationService` does: it
 * assembles a request, it does not decide what is allowed.
 */

/** Exactly the fields the Edit Character screen may change. Nothing else. */
export interface EditDraft {
  name: string;
  crawlerType: CrawlerType;
  size: number;
  stats: StatBlock;
  level: number;
  floor: number;
  aiFavor: number;
  notes: string;
  skills: CharacterSkill[];
}

export function draftFromCharacter(character: Character): EditDraft {
  return {
    name: character.name,
    crawlerType: character.crawlerType,
    size: character.size,
    stats: { ...character.unenhancedStats },
    level: character.level,
    floor: character.floor,
    aiFavor: character.aiFavor,
    notes: character.notes,
    // Copied, so editing the draft can never reach back into the saved record.
    skills: character.skills.map((s) => ({ ...s })),
  };
}

/**
 * The minimal batch that turns `character` into `draft`.
 *
 * Order is deliberate and load-bearing:
 *
 *  1. Skills the crawler is losing go FIRST, so switching a crawler from animal
 *     to human works in one batch once its animal-only Skills are dropped
 *     (RULE DCC-EDIT-002).
 *  2. Size and crawler type are emitted in whichever order leaves the crawler
 *     legal at every step, because each is checked against the state produced
 *     by the change before it (C&C p.16, Table 10).
 *  3. Skills the crawler is GAINING go last, so they are checked against the
 *     new crawler type rather than the old one.
 *
 * When no legal ordering exists the rules layer refuses the whole batch, which
 * is the correct outcome: the crawler is never left half-edited.
 */
export function changesFromDraft(character: Character, draft: EditDraft): CharacterChange[] {
  const changes: CharacterChange[] = [];

  if (draft.name.trim() !== character.name) {
    changes.push({ kind: 'set-name', name: draft.name.trim() });
  }

  const draftIds = new Set(draft.skills.map((s) => s.skillId));
  for (const existing of character.skills) {
    if (!draftIds.has(existing.skillId)) {
      changes.push({ kind: 'remove-skill', skillId: existing.skillId });
    }
  }

  changes.push(...sizeAndTypeChanges(character, draft));

  for (const stat of STAT_IDS) {
    if (draft.stats[stat] !== character.unenhancedStats[stat]) {
      changes.push({ kind: 'set-stat', stat, value: draft.stats[stat] });
    }
  }

  if (draft.level !== character.level) changes.push({ kind: 'set-level', level: draft.level });
  if (draft.floor !== character.floor) changes.push({ kind: 'set-floor', floor: draft.floor });
  if (draft.aiFavor !== character.aiFavor) {
    changes.push({ kind: 'set-ai-favor', aiFavor: draft.aiFavor });
  }

  for (const entry of draft.skills) {
    const before = character.skills.find((s) => s.skillId === entry.skillId);
    if (!before) continue;
    if (before.rank !== entry.rank) {
      changes.push({ kind: 'set-skill-rank', skillId: entry.skillId, rank: entry.rank });
    }
  }

  for (const entry of draft.skills) {
    if (!character.skills.some((s) => s.skillId === entry.skillId)) {
      changes.push({ kind: 'add-skill', skillId: entry.skillId, rank: entry.rank });
    }
  }

  if (draft.notes !== character.notes) changes.push({ kind: 'set-notes', notes: draft.notes });

  return changes;
}

/**
 * Size and crawler type constrain each other, so the pair has to be ordered.
 *
 * `set-crawler-type` is refused when the size the crawler currently holds is
 * illegal for the new type, and `set-size` is refused when the size is illegal
 * for the type the crawler currently holds. Emitting the size first works
 * whenever the new size is already legal under the OLD type (animal 2 -> human:
 * size 4 is legal for an animal, so set it, then switch). Otherwise the type
 * has to move first (human 4 -> animal 2: 4 is legal for an animal, so switch,
 * then shrink).
 */
function sizeAndTypeChanges(character: Character, draft: EditDraft): CharacterChange[] {
  const typeChanged = draft.crawlerType !== character.crawlerType;
  const sizeChanged = draft.size !== character.size;

  if (!typeChanged && !sizeChanged) return [];
  if (!typeChanged) return [{ kind: 'set-size', size: draft.size }];
  if (!sizeChanged) return [{ kind: 'set-crawler-type', crawlerType: draft.crawlerType }];

  const sizeFirst = isLegalSize(character.crawlerType, draft.size);
  return sizeFirst
    ? [
        { kind: 'set-size', size: draft.size },
        { kind: 'set-crawler-type', crawlerType: draft.crawlerType },
      ]
    : [
        { kind: 'set-crawler-type', crawlerType: draft.crawlerType },
        { kind: 'set-size', size: draft.size },
      ];
}

/**
 * Skills the crawler could be given, for the "add a Skill" picker.
 *
 * Filtered by the crawler type in the DRAFT, not the saved record, so a player
 * who has just switched to an animal crawler immediately sees the animal-only
 * Skills. The rules layer checks this again on commit; this only keeps illegal
 * options out of the list.
 */
export function addableSkills(draft: EditDraft, registry: ContentRegistry): Skill[] {
  const held = new Set(draft.skills.map((s) => s.skillId));
  return registry
    .allSkills()
    .filter((skill) => !held.has(skill.id))
    .filter((skill) => isSkillLegalFor(skill, draft.crawlerType))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Skills in the draft that the draft's own crawler type may not have.
 *
 * The screen uses this to warn BEFORE the player asks for a review, so a
 * refused batch is not the first they hear of the problem.
 */
export function illegalDraftSkills(draft: EditDraft, registry: ContentRegistry): Skill[] {
  return draft.skills
    .map((s) => registry.getSkill(s.skillId))
    .filter((s): s is Skill => s !== undefined)
    .filter((s) => !isSkillLegalFor(s, draft.crawlerType));
}
